"use client";

import { Send } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { useSendParcel } from "./SendParcelProvider";

type Variant = "primary" | "hero" | "white" | "block" | "compact";

const VARIANTS: Record<Variant, string> = {
  // Header
  compact:
    "gap-2 rounded-full bg-gradient-cta px-[18px] py-2.5 text-sm font-bold text-white shadow-[0_10px_22px_-10px_rgba(192,87,61,.7)] hover:-translate-y-0.5 hover:shadow-[0_18px_38px_-12px_rgba(192,87,61,.65)]",
  // Hero
  hero: "gap-[11px] rounded-[15px] bg-gradient-cta px-[30px] py-[17px] text-lg font-bold text-white shadow-[0_18px_38px_-14px_rgba(192,87,61,.7)] hover:-translate-y-0.5",
  // Generic primary
  primary:
    "gap-2.5 rounded-btn bg-gradient-cta px-6 py-3.5 text-base font-bold text-white shadow-[0_14px_30px_-14px_rgba(192,87,61,.7)] hover:-translate-y-0.5",
  // On terracotta backgrounds (final CTA)
  white:
    "gap-[11px] rounded-[15px] bg-white px-9 py-[18px] text-[19px] font-bold text-terracotta shadow-[0_20px_42px_-16px_rgba(0,0,0,.42)] hover:-translate-y-0.5",
  // Full width (estimator, drawer, sticky)
  block:
    "w-full gap-2.5 rounded-btn bg-gradient-cta px-5 py-[15px] text-base font-bold text-white shadow-[0_12px_26px_-12px_rgba(192,87,61,.7)] hover:-translate-y-0.5",
};

const ICON_SIZE: Record<Variant, number> = {
  compact: 16,
  hero: 20,
  primary: 18,
  white: 20,
  block: 17,
};

export function SendParcelButton({
  variant = "primary",
  className,
  label,
}: {
  variant?: Variant;
  className?: string;
  label?: string;
}) {
  const { sendParcel } = useSendParcel();
  const t = useTranslations("send");

  return (
    <button
      onClick={() => {
        track("send_parcel_click", { variant });
        sendParcel();
      }}
      className={cn(
        "group inline-flex cursor-pointer items-center justify-center font-sans transition-transform active:translate-y-0",
        VARIANTS[variant],
        className,
      )}
    >
      <Send
        size={ICON_SIZE[variant]}
        className="transition-transform duration-200 group-hover:translate-x-0.5"
      />
      {label ?? t("cta")}
    </button>
  );
}
